/**
 * The media half of the in-iframe bridge: turns `genie:input` payloads into the
 * `window.genie` values the contract promises (see `GENIE_JS_API`).
 *
 * The returned text is spliced into the bridge's IIFE and relies on its scope:
 * `paused`, `handlers` and `reportError`. It defines `mediaInput(handle, data)`,
 * `mediaPause()`, `mediaResume()`, `silentAudio()` and `modelToThree(THREE)`,
 * and exposes `window.__genieMedia` for the GLSL bootstrap.
 */

import { AUDIO_BIN_COUNT, AUDIO_SILENCE_DB } from './contract'

export function buildBridgeMediaCode(): string {
  return `
  // --- Media inputs (media/contract.ts) ---
  var AUDIO_BINS = ${AUDIO_BIN_COUNT}, SILENCE_DB = ${AUDIO_SILENCE_DB};
  var media = window.__genieMedia = { audioConnected: false };
  var videoElement = null, videoUrl = null;

  // Silence is the decibel floor, not 0 dB: 0 dB is full scale and would draw every bar at maximum.
  function silentAudio() {
    var fft = new Float32Array(AUDIO_BINS);
    fft.fill(SILENCE_DB);
    return { fft: fft, waveform: new Float32Array(AUDIO_BINS), amplitude: 0 };
  }

  function notifyInput(handle, value) {
    var list = handlers[handle] || [];
    for (var i = 0; i < list.length; i++) {
      try { list[i](value); } catch (e) { reportError(e && e.message, 'runtime'); }
    }
  }

  function copySamples(target, source, fallback) {
    var n = source && typeof source.length === 'number' ? Math.min(target.length, source.length) : 0;
    for (var i = 0; i < n; i++) { var v = +source[i]; target[i] = isFinite(v) ? v : fallback; }
    for (var j = n; j < target.length; j++) target[j] = fallback;
  }

  // Frames arrive ~30 times a second: fill the same arrays so sketches can hold on to them.
  function setAudio(data) {
    var audio = window.genie.audio;
    if (!(audio.fft instanceof Float32Array) || audio.fft.length !== AUDIO_BINS) audio.fft = new Float32Array(AUDIO_BINS);
    if (!(audio.waveform instanceof Float32Array) || audio.waveform.length !== AUDIO_BINS) audio.waveform = new Float32Array(AUDIO_BINS);
    if (!data || typeof data !== 'object') {
      media.audioConnected = false;
      audio.fft.fill(SILENCE_DB);
      audio.waveform.fill(0);
      audio.amplitude = 0;
      return null;
    }
    media.audioConnected = true;
    copySamples(audio.fft, data.fft, SILENCE_DB);
    copySamples(audio.waveform, data.waveform, 0);
    var amplitude = +data.amplitude;
    audio.amplitude = isFinite(amplitude) ? Math.max(0, Math.min(1, amplitude)) : 0;
    return audio;
  }

  function clearVideo() {
    var el = videoElement;
    videoElement = null;
    window.genie.inputVideo = null;
    if (el) {
      el.pause();
      el.removeAttribute('src');
      try { el.load(); } catch (e) {}
      if (el.parentNode) el.parentNode.removeChild(el);
    }
    if (videoUrl) { URL.revokeObjectURL(videoUrl); videoUrl = null; }
  }

  function mediaResume() {
    if (!videoElement || paused) return;
    var playing = videoElement.play();
    if (playing && playing.catch) playing.catch(function() {});
  }

  function mediaPause() {
    if (videoElement) videoElement.pause();
  }

  // Browsers pause video-only media in hidden tabs; pick it back up when the page shows again.
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible') mediaResume();
  });

  // Video arrives as bytes (the sandbox can't read the host's URLs) and plays from a local blob.
  function setVideo(data) {
    clearVideo();
    if (!data || typeof data !== 'object' || !data.bytes) { notifyInput('video', null); return; }
    var el = document.createElement('video');
    el.muted = true;
    el.defaultMuted = true;
    el.loop = true;
    el.playsInline = true;
    el.preload = 'auto';
    el.setAttribute('muted', '');
    el.setAttribute('playsinline', '');
    // In the document but under the page background: browsers keep decoding frames for it,
    // and readers driven by requestVideoFrameCallback (THREE.VideoTexture) keep updating.
    // No CSS size: p5's image() scales by the element's CSS width. Its width/height attributes
    // take the video's own size once known, which p5 reads as the image size.
    el.style.cssText = 'position:fixed;left:0;top:0;z-index:-1;pointer-events:none;';
    // p5's image() draws an element through its .elt, so the bare element works there too.
    el.elt = el;
    videoUrl = URL.createObjectURL(new Blob([data.bytes], { type: data.mimeType || 'video/mp4' }));
    videoElement = el;
    el.addEventListener('loadeddata', function() {
      if (videoElement !== el || window.genie.inputVideo === el) return;
      el.width = el.videoWidth;
      el.height = el.videoHeight;
      window.genie.inputVideo = el;
      notifyInput('video', el);
    });
    el.addEventListener('error', function() {
      if (videoElement === el) reportError('The video input could not be decoded', 'input');
    });
    el.src = videoUrl;
    document.body.appendChild(el);
    mediaResume();
  }

  function setModel(data) {
    window.genie.inputModel = data && typeof data === 'object' && data.meshes && data.meshes.length && data.bounds ? data : null;
    return window.genie.inputModel;
  }

  // The 3D input as three.js meshes, centred on the origin and scaled into a unit sphere.
  // The outer group is the caller's to move and rotate; the inner one holds the fit.
  function modelToThree(THREE) {
    var model = window.genie.inputModel;
    if (!model || !THREE) return null;
    var min = model.bounds.min, max = model.bounds.max;
    var dx = max[0] - min[0], dy = max[1] - min[1], dz = max[2] - min[2];
    var scale = 1 / (Math.sqrt(dx * dx + dy * dy + dz * dz) / 2 || 1);
    var fit = new THREE.Group();
    fit.scale.setScalar(scale);
    fit.position.set(-(min[0] + max[0]) / 2 * scale, -(min[1] + max[1]) / 2 * scale, -(min[2] + max[2]) / 2 * scale);
    var material = new THREE.MeshStandardMaterial({ color: 0xd9d9d9, roughness: 0.55, metalness: 0.1 });
    for (var i = 0; i < model.meshes.length; i++) {
      var mesh = model.meshes[i];
      if (!mesh.positions || mesh.positions.length < 9) continue;
      var geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(mesh.positions, 3));
      var hasNormals = mesh.normals && mesh.normals.length === mesh.positions.length;
      if (hasNormals) geometry.setAttribute('normal', new THREE.BufferAttribute(mesh.normals, 3));
      if (mesh.uvs && mesh.uvs.length / 2 === mesh.positions.length / 3) geometry.setAttribute('uv', new THREE.BufferAttribute(mesh.uvs, 2));
      if (mesh.indices && mesh.indices.length) geometry.setIndex(new THREE.BufferAttribute(mesh.indices, 1));
      if (!hasNormals) geometry.computeVertexNormals();
      fit.add(new THREE.Mesh(geometry, material));
    }
    var group = new THREE.Group();
    group.name = 'genie-model';
    group.add(fit);
    return group;
  }

  function mediaInput(handle, data) {
    if (handle === 'video') { setVideo(data); return; }
    var value = data;
    if (handle === 'image') window.genie.inputImage = data;
    else if (handle === 'text') value = window.genie.inputText = typeof data === 'string' ? data : null;
    else if (handle === '3d') value = setModel(data);
    else if (handle === 'audio') value = setAudio(data);
    notifyInput(handle, value);
  }
`
}
